import { useApolloClient } from "@apollo/client/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDisplayLocale } from "~/common/functions/useDisplayLocale";
import type { RecordsBoolExp } from "~/common/types/graphql";
import type { TimelineEntity } from "~/components/timeline";
import {
  GetRelationsForTimelineDocument,
  type GetRelationsForTimelineQuery,
  type GetRelationsForTimelineQueryVariables,
} from "~/features/relations/gqls";
import {
  BUFFER_CHUNKS,
  CACHE_TTL_MS,
  chunkIdsForRange,
  chunkJDNBounds,
  DEFAULT_RANGE_YEARS,
  MAX_CONCURRENT_FETCHES,
  MAX_FILTER_HASHES,
} from "~/features/relations/utils/timelineChunks";
import { toTimelineRelation } from "~/features/relations/utils/toTimelineRelation";
import { gdtfYearRangeWithPadding, parseGdtfRange } from "~/utils/gdtfRange";

const CACHE_KEY = "dewey.v2.timeline.relations.chunks";

type ChunkData = { entities: TimelineEntity[]; loadedAt: number };
type ChunkCache = Record<string, Record<string, ChunkData>>;

const readCache = (): ChunkCache => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as ChunkCache) : {};
  } catch {
    return {};
  }
};

const writeCache = (cache: ChunkCache): void => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    // quota exceeded — silently ignore, in-memory still works
  }
};

const evictStale = (cache: ChunkCache): ChunkCache => {
  const now = Date.now();
  const result: ChunkCache = {};
  for (const [fh, chunks] of Object.entries(cache)) {
    const fresh: Record<string, ChunkData> = {};
    for (const [cid, data] of Object.entries(chunks)) {
      if (now - data.loadedAt < CACHE_TTL_MS) fresh[cid] = data;
    }
    if (Object.keys(fresh).length > 0) result[fh] = fresh;
  }
  return result;
};

const evictOldestHashes = (cache: ChunkCache): ChunkCache => {
  const keys = Object.keys(cache);
  if (keys.length <= MAX_FILTER_HASHES) return cache;
  const withAge = keys.map((k) => ({ k, oldest: Math.min(...Object.values(cache[k]).map((d) => d.loadedAt)) }));
  withAge.sort((a, b) => a.oldest - b.oldest);
  const keep = new Set(withAge.slice(keys.length - MAX_FILTER_HASHES).map((x) => x.k));
  return Object.fromEntries(Object.entries(cache).filter(([k]) => keep.has(k)));
};

/** Flatten loaded chunks within the visible window into a sorted, row-numbered list. */
const deriveDisplayEntities = (
  allChunks: Map<number, TimelineEntity[]>,
  visibleChunkIds: Set<number>,
): TimelineEntity[] => {
  const seen = new Set<string>();
  const all: TimelineEntity[] = [];
  for (const [id, ents] of allChunks) {
    if (visibleChunkIds.has(id)) {
      for (const e of ents) {
        if (!seen.has(e.id)) {
          seen.add(e.id);
          all.push(e);
        }
      }
    }
  }
  all.sort((a, b) => a.start.year - b.start.year);
  return all.map((e, i) => ({ ...e, rowNumber: i }));
};

const makeDefaultRange = (): [number, number] => {
  const currentYear = new Date().getFullYear();
  return [currentYear - DEFAULT_RANGE_YEARS, currentYear];
};

const gdtfToYearRange = (gdtfFrom?: string, gdtfTo?: string): [number, number] | null =>
  gdtfYearRangeWithPadding(parseGdtfRange(gdtfFrom, gdtfTo), DEFAULT_RANGE_YEARS);

interface Options {
  tenantId: string;
  baseWhere: RecordsBoolExp;
  filterHash: string;
  orderBy: GetRelationsForTimelineQueryVariables["order_by"];
  gdtfFrom?: string;
  gdtfTo?: string;
}

interface Result {
  entities: TimelineEntity[];
  loading: boolean;
  onVisibleYearRangeChange: (range: [number, number]) => void;
}

/**
 * Progressive, viewport-bounded timeline loader. Records are fetched per 100-year
 * chunk (by JDN bounds) only as they enter the visible range, cached in localStorage,
 * and kept in a per-chunk Map so memory stays bounded regardless of panning history.
 * Ported from V1 `useChunkedTimelineLoader`.
 */
export function useChunkedTimelineLoader({
  tenantId,
  baseWhere,
  filterHash,
  orderBy,
  gdtfFrom,
  gdtfTo,
}: Options): Result {
  const client = useApolloClient();
  const locale = useDisplayLocale();
  const [entities, setEntities] = useState<TimelineEntity[]>([]);
  const [loading, setLoading] = useState(false);

  const allChunksRef = useRef<Map<number, TimelineEntity[]>>(new Map());
  const visibleChunkIdsRef = useRef<Set<number>>(new Set(chunkIdsForRange(makeDefaultRange(), BUFFER_CHUNKS)));
  const loadedRef = useRef<Set<string>>(new Set());
  const filterHashRef = useRef(filterHash);
  const inflightRef = useRef(0);

  const tenantIdRef = useRef(tenantId);
  tenantIdRef.current = tenantId;
  const baseWhereRef = useRef(baseWhere);
  baseWhereRef.current = baseWhere;
  const orderByRef = useRef(orderBy);
  orderByRef.current = orderBy;

  const updateDisplay = useCallback(() => {
    setEntities(deriveDisplayEntities(allChunksRef.current, visibleChunkIdsRef.current));
  }, []);
  const updateDisplayRef = useRef(updateDisplay);
  updateDisplayRef.current = updateDisplay;

  const loadChunks = useCallback(
    async (chunkIds: number[]) => {
      const currentHash = filterHashRef.current;
      const cacheKey = `${currentHash}::${locale}`;
      const loaded = loadedRef.current;
      const missing = chunkIds.filter((id) => !loaded.has(`${currentHash}:${id}`));
      if (missing.length === 0) return;

      const filterCache = evictStale(readCache())[cacheKey] ?? {};
      const toFetch: number[] = [];
      for (const id of missing) {
        const hit = filterCache[String(id)];
        if (hit) {
          allChunksRef.current.set(id, hit.entities);
          loaded.add(`${currentHash}:${id}`);
        } else {
          toFetch.push(id);
        }
      }
      if (missing.length > toFetch.length) updateDisplayRef.current();
      if (toFetch.length === 0) return;

      inflightRef.current += 1;
      setLoading(true);
      try {
        const newChunkEntries: Record<string, ChunkData> = {};
        for (let i = 0; i < toFetch.length; i += MAX_CONCURRENT_FETCHES) {
          if (filterHashRef.current !== currentHash) break;
          const batch = toFetch.slice(i, i + MAX_CONCURRENT_FETCHES);
          await Promise.all(
            batch.map(async (chunkId) => {
              const [jdnGte, jdnLte] = chunkJDNBounds(chunkId);
              const where: RecordsBoolExp = {
                ...baseWhereRef.current,
                dateJdn: { _isNull: false, _gte: jdnGte, _lte: jdnLte },
              };
              const result = await client.query<GetRelationsForTimelineQuery, GetRelationsForTimelineQueryVariables>({
                query: GetRelationsForTimelineDocument,
                variables: { tenantId: tenantIdRef.current, where, order_by: orderByRef.current },
                fetchPolicy: "no-cache",
              });
              if (filterHashRef.current !== currentHash) return;
              const newEntities = (result.data?.records ?? []).reduce<TimelineEntity[]>((acc, record, idx) => {
                const entity = toTimelineRelation(record, idx, locale);
                if (entity) acc.push(entity);
                return acc;
              }, []);
              allChunksRef.current.set(chunkId, newEntities);
              newChunkEntries[String(chunkId)] = { entities: newEntities, loadedAt: Date.now() };
              loaded.add(`${currentHash}:${chunkId}`);
            }),
          );
          if (filterHashRef.current === currentHash) updateDisplayRef.current();
        }
        if (Object.keys(newChunkEntries).length > 0 && filterHashRef.current === currentHash) {
          const freshCache = evictStale(readCache());
          const freshFilterCache = freshCache[cacheKey] ?? {};
          writeCache(evictOldestHashes({ ...freshCache, [cacheKey]: { ...freshFilterCache, ...newChunkEntries } }));
        }
      } finally {
        inflightRef.current -= 1;
        if (inflightRef.current === 0) setLoading(false);
      }
    },
    [client, locale],
  );
  const loadChunksRef = useRef(loadChunks);
  loadChunksRef.current = loadChunks;

  const gdtfFromRef = useRef(gdtfFrom);
  gdtfFromRef.current = gdtfFrom;
  const gdtfToRef = useRef(gdtfTo);
  gdtfToRef.current = gdtfTo;

  useEffect(() => {
    filterHashRef.current = filterHash;
    loadedRef.current = new Set();
    allChunksRef.current = new Map();
    const initialRange = gdtfToYearRange(gdtfFromRef.current, gdtfToRef.current) ?? makeDefaultRange();
    visibleChunkIdsRef.current = new Set(chunkIdsForRange(initialRange, BUFFER_CHUNKS));
    setEntities([]);
    void loadChunksRef.current(chunkIdsForRange(initialRange, BUFFER_CHUNKS));
  }, [filterHash, locale]);

  const onVisibleYearRangeChange = useCallback((range: [number, number]) => {
    const chunkIds = chunkIdsForRange(range, BUFFER_CHUNKS);
    visibleChunkIdsRef.current = new Set(chunkIds);
    updateDisplayRef.current();
    void loadChunksRef.current(chunkIds);
  }, []);

  return { entities, loading, onVisibleYearRangeChange };
}
